import type { ScoMemberProfileStatus, ScoMemberProfileStatusKey } from '@src/graphql-types'
import { gql } from '@src/lib/gql'
import { startupsUrl } from '@src/lib/startups-url'

/**
 * Viewer-aware status for `/members/[slug]` and its aliases (Advisors cleanup L5–L7).
 * Read server-side in `getServerSideProps` (status code) and in the tree (canonical / robots).
 */
export const MEMBER_PROFILE_STATUS = gql`
  query MemberProfileStatus($slugOrId: String!) {
    memberProfileStatus(slugOrId: $slugOrId) {
      status
      slug
      isIndexable
      isBookable
    }
  }
`

export type MemberProfileStatusProps = Pick<ScoMemberProfileStatus, 'slug' | 'isIndexable' | 'isBookable'> & {
  /** The generated enum or its string value (`'VISIBLE'`, …). */
  status: ScoMemberProfileStatusKey | `${ScoMemberProfileStatusKey}`
}

export type MemberProfileHttpOutcome = { notFound: true } | { statusCode: 200 | 410 }

/**
 * L7: VISIBLE → 200, UNAVAILABLE (hidden / private / banned / deleted for this viewer) → 410,
 * NOT_FOUND (never existed) → 404. An unknown or missing status renders normally (200); the API
 * still enforces visibility on every field, and the page falls back to `noindex` (see
 * `memberProfileSeo`), so a failed status read never makes a profile indexable.
 */
export const memberProfileHttpOutcome = (
  status: ScoMemberProfileStatusKey | `${ScoMemberProfileStatusKey}` | null | undefined
): MemberProfileHttpOutcome => {
  if (status === 'NOT_FOUND') {
    return { notFound: true }
  }
  if (status === 'UNAVAILABLE') {
    return { statusCode: 410 }
  }
  return { statusCode: 200 }
}

/** L5: the one canonical public profile URL, on the site host (`NEXT_PUBLIC_NEXT_URL`). */
export const memberProfileCanonicalUrl = (slug: string): string =>
  startupsUrl(`/members/${encodeURIComponent(slug)}`)

/** `[memberSlug]` route param as a string ('' when missing). */
export const memberSlugParam = (value: string | string[] | undefined): string =>
  (Array.isArray(value) ? value[0] : value || '').trim()
